import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

(() => {
  const translateBriutlyShell = () => {
    document.querySelectorAll('[data-testid="header-logo"], h1.visually-hidden').forEach((node) => {
      if (node.textContent.trim().toLowerCase() === 'briutly') {
        node.textContent = 'בריאותלי';
      }
    });

    document.querySelectorAll('.footer-utilities__text').forEach((node) => {
      if (node.textContent.includes('briutly') || node.textContent.includes('Powered by Shopify') || node.textContent.includes('מופעל ע')) {
        node.innerHTML = '&copy; ' + new Date().getFullYear() + ' <a href="/">בריאותלי</a>';
      }
    });

    document.querySelectorAll('h2').forEach((node) => {
      if (node.textContent.trim() === 'Join our email list') {
        node.textContent = 'הצטרפי לרשימת הדיוור';
      }
    });

    document.querySelectorAll('p').forEach((node) => {
      if (node.textContent.trim() === 'Get exclusive deals and early access to new products.') {
        node.textContent = 'עדכונים, מבצעים ותוכן קצר שיעזור לך לבנות שגרת בריאות פשוטה יותר.';
      }
    });

    document.querySelectorAll('[aria-label="Sign up"]').forEach((node) => {
      node.setAttribute('aria-label', 'הרשמה');
    });
  };

  onDocumentLoaded(translateBriutlyShell);
  window.setTimeout(translateBriutlyShell, 300);
  window.setTimeout(translateBriutlyShell, 1200);
})();

(() => {
  const softenMyBalanceOfferProof = () => {
    if (!document.querySelector('.mb-pr')) return;

    const styleId = 'my-balance-offer-proof-softener';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .mb-pr .mb-pink-panel {
          position: relative !important;
          overflow: hidden !important;
          border: 1px solid rgba(26,107,58,.16) !important;
          border-right: 1px solid rgba(26,107,58,.16) !important;
          border-radius: 10px !important;
          background: linear-gradient(135deg, rgba(232,248,216,.86), rgba(255,255,255,.98) 54%, rgba(255,250,241,.92)) !important;
          box-shadow: 0 18px 42px rgba(26,107,58,.08) !important;
          color: #1e1f24 !important;
        }
        .mb-pr .mb-pink-panel::before {
          content: "תוצאה אמיתית מהתהליך" !important;
          display: inline-flex !important;
          align-items: center !important;
          margin-bottom: 11px !important;
          border: 1px solid rgba(26,107,58,.18) !important;
          border-radius: 999px !important;
          background: rgba(255,255,255,.72) !important;
          color: #0a4a25 !important;
          padding: 6px 10px !important;
          font-size: 11px !important;
          font-weight: 800 !important;
        }
        .mb-pr .mb-pink-panel h3 {
          color: #0a4a25 !important;
          font-size: clamp(20px, 2.1vw, 25px) !important;
          line-height: 1.18 !important;
        }
        .mb-pr .mb-pink-panel p {
          color: #364238 !important;
          font-size: 14.5px !important;
          line-height: 1.75 !important;
        }
      `;
      document.head.appendChild(style);
    }
  };

  onDocumentLoaded(softenMyBalanceOfferProof);
  window.setTimeout(softenMyBalanceOfferProof, 300);
  window.setTimeout(softenMyBalanceOfferProof, 1200);
})();

(() => {
  const refineMyBalanceHeader = () => {
    if (!document.querySelector('.mb-pr')) return;

    const styleId = 'my-balance-header-refinement';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        html.template-my-balance-pr [id*="header_announcements"],
        html.template-my-balance-pr [id*="header_announcements"] .section,
        html.template-my-balance-pr [id*="header_announcements"] .section-background {
          background: #2f2f2f !important;
          border: 0 !important;
        }
        html.template-my-balance-pr [id*="header_announcements"] .section {
          min-height: 38px !important;
          padding-block: 0 !important;
        }
        html.template-my-balance-pr [id*="header_announcements"] .announcement-bar__text,
        html.template-my-balance-pr [id*="header_announcements"] * {
          color: #fff !important;
          font-family: var(--font-subheading--family), var(--font-body--family), Inter, Arial, sans-serif !important;
          font-size: .75rem !important;
          font-weight: var(--font-subheading--weight) !important;
          letter-spacing: 0 !important;
        }
        html.template-my-balance-pr .header--my-balance-pr {
          background: #fff !important;
          border: 0 !important;
          border-bottom: 1px solid rgba(17, 17, 17, .08) !important;
          box-shadow: none !important;
        }
        html.template-my-balance-pr .header-section:has(> .header--my-balance-pr),
        html.template-my-balance-pr #header-group:has(.header--my-balance-pr) {
          position: static !important;
          top: auto !important;
          z-index: auto !important;
        }
        html.template-my-balance-pr .header--my-balance-pr :is(.header__drawer, .header-menu, .dropdown-localization, .search-action, .header__navigation-bar-row, .account-button) {
          display: none !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .header__columns {
          position: static !important;
          width: 100% !important;
          max-width: none !important;
          min-height: 72px !important;
          justify-content: center !important;
          margin-inline: 0 !important;
          padding-inline: clamp(24px, 3vw, 56px) !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .header__row--top,
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-header__inner {
          position: static !important;
          width: 100% !important;
          max-width: none !important;
          min-height: 72px !important;
          margin-inline: 0 !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-logo {
          position: absolute !important;
          left: clamp(40px, 3.5vw, 58px) !important;
          right: auto !important;
          top: 50% !important;
          z-index: 20 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          text-decoration: none !important;
          transform: translateY(-50%) !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-logo__image {
          display: block !important;
          width: 118px !important;
          height: auto !important;
          max-height: 26px !important;
          object-fit: contain !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-logo:not(:has(.mb-shopify-logo__image)) {
          width: 118px !important;
          height: 26px !important;
          overflow: hidden !important;
          color: transparent !important;
          font-size: 0 !important;
          background: url("/cdn/shop/t/3/assets/briutly-logo-elegant.svg") left center / contain no-repeat !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .header-logo {
          display: none !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .header-logo {
          position: absolute !important;
          left: clamp(28px, 3.2vw, 58px) !important;
          right: auto !important;
          top: 50% !important;
          margin: 0 !important;
          transform: translateY(-50%) !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .header-logo__image--my-balance,
        html.template-my-balance-pr .header--my-balance-pr .header-logo__image {
          width: min(118px, 28vw) !important;
          max-height: 30px !important;
          height: auto !important;
          object-fit: contain !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-header__right {
          position: absolute !important;
          right: clamp(40px, 3.5vw, 58px) !important;
          left: auto !important;
          top: 50% !important;
          z-index: 20 !important;
          display: flex !important;
          align-items: center !important;
          gap: clamp(20px, 3vw, 38px) !important;
          transform: translateY(-50%) !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-nav {
          display: flex !important;
          align-items: center !important;
          gap: clamp(18px, 2.2vw, 30px) !important;
          direction: rtl !important;
          white-space: nowrap !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-nav a {
          color: #242424 !important;
          font-family: var(--font-body--family), Inter, Arial, sans-serif !important;
          font-size: .875rem !important;
          font-style: var(--font-body--style) !important;
          font-weight: var(--font-body--weight) !important;
          line-height: 1 !important;
          letter-spacing: 0 !important;
          text-decoration: none !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-cart {
          position: relative !important;
          z-index: 20 !important;
          display: inline-flex !important;
          width: 44px !important;
          height: 44px !important;
          align-items: center !important;
          justify-content: center !important;
          border: 0 !important;
          border-radius: 0 !important;
          color: #101820 !important;
          background: transparent !important;
          text-decoration: none !important;
          box-shadow: none !important;
        }
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-cart .svg-wrapper,
        html.template-my-balance-pr .header--my-balance-pr .mb-shopify-cart svg {
          display: block !important;
          width: 20px !important;
          height: 20px !important;
        }
        html.template-my-balance-pr .mb-pr .mb-notice {
          display: none !important;
        }
        html.template-my-balance-pr .mb-pr .mb-hero {
          margin-top: 0 !important;
        }
        @media (max-width: 640px) {
          html.template-my-balance-pr .header--my-balance-pr .header__columns {
            min-height: 86px !important;
            padding-inline: 16px !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .header__row--top,
          html.template-my-balance-pr .header--my-balance-pr .mb-shopify-header__inner {
            min-height: 86px !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .mb-shopify-logo {
            left: 16px !important;
            top: 24px !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .mb-shopify-logo__image {
            width: 104px !important;
            max-height: 24px !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .mb-shopify-logo:not(:has(.mb-shopify-logo__image)) {
            width: 104px !important;
            height: 24px !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .header-logo__image--my-balance,
          html.template-my-balance-pr .header--my-balance-pr .header-logo__image {
            width: 112px !important;
            max-height: 29px !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .mb-shopify-header__right {
            right: 16px !important;
            top: 25px !important;
            gap: 14px !important;
            transform: none !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .mb-shopify-nav {
            position: absolute !important;
            right: 0 !important;
            top: 35px !important;
            gap: 16px !important;
            width: calc(100vw - 32px) !important;
            justify-content: flex-start !important;
            overflow-x: auto !important;
            padding-bottom: 8px !important;
            scrollbar-width: none !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .mb-shopify-nav::-webkit-scrollbar {
            display: none !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .mb-shopify-nav a {
            font-size: 12px !important;
          }
          html.template-my-balance-pr .header--my-balance-pr .mb-shopify-cart {
            width: 36px !important;
            height: 36px !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    initBriutlyAnnouncementRotator();

    const logo = document.querySelector('.header--my-balance-pr .mb-shopify-logo');
    if (logo && !logo.querySelector('.mb-shopify-logo__image')) {
      logo.innerHTML = '<img class="mb-shopify-logo__image" src="/cdn/shop/t/3/assets/briutly-logo-elegant.svg" alt="בריאותלי - Briutly" width="224" height="48" loading="eager">';
    }

    const header = document.querySelector('.header--my-balance-pr .mb-shopify-header__inner, .header--my-balance-pr .header__columns');
    if (!header || header.querySelector('.mb-shopify-header__right')) return;

    header.insertAdjacentHTML('beforeend', `
      <a class="mb-shopify-logo" href="/" aria-label="בריאותלי">
        <img class="mb-shopify-logo__image" src="/cdn/shop/t/3/assets/briutly-logo-elegant.svg" alt="בריאותלי - Briutly" width="224" height="48" loading="eager">
      </a>
      <div class="mb-shopify-header__right">
        <a class="mb-shopify-cart" href="/cart" aria-label="סל קניות">
          <span class="svg-wrapper">
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M3.392 6.875h13.216v8.016c0 .567-.224 1.112-.624 1.513-.4.402-.941.627-1.506.627H5.522a2.13 2.13 0 0 1-1.506-.627 2.15 2.15 0 0 1-.624-1.513zM8.818 2.969h2.333c.618 0 1.211.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.313c0-.622.246-1.218.683-1.658a2.33 2.33 0 0 1 1.65-.686"/>
            </svg>
          </span>
        </a>
        <nav class="mb-shopify-nav" aria-label="ניווט ראשי">
          <a href="/">דף הבית</a>
          <a href="/collections/all">המוצרים שלנו</a>
          <a href="/pages/contact">צור קשר</a>
        </nav>
      </div>
    `);
  };

  onDocumentLoaded(refineMyBalanceHeader);
  window.setTimeout(refineMyBalanceHeader, 300);
  window.setTimeout(refineMyBalanceHeader, 1200);
})();

(() => {
  const messages = [
    'מעל ל-35,750 לקוחות',
    'משלוח מהיר וחינם',
    'אחריות לשביעות רצון או כספך חזרה',
    'תמיכה וליווי קבועים בווטסאפ',
  ];

  window.initBriutlyAnnouncementRotator = () => {
    const bars = document.querySelectorAll('[id*="header_announcements"] .announcement-bar__text');
    if (!bars.length) return;

    if (!document.getElementById('briutly-announcement-rotator-style')) {
      const style = document.createElement('style');
      style.id = 'briutly-announcement-rotator-style';
      style.textContent = `
        [id*="header_announcements"] .announcement-bar__text {
          direction: rtl !important;
          text-align: center !important;
          min-height: 1.25em !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          white-space: nowrap !important;
        }
        [data-briutly-announcement-rotator] {
          display: inline-block !important;
          opacity: 1;
          transform: translateY(0);
          transition: opacity 220ms ease, transform 220ms ease;
        }
        [data-briutly-announcement-rotator].is-changing {
          opacity: 0;
          transform: translateY(-4px);
        }
        @media (max-width: 640px) {
          [id*="header_announcements"] .announcement-bar__text {
            width: 100% !important;
            white-space: normal !important;
            line-height: 1.35 !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-briutly-announcement-rotator] {
            transition: none !important;
          }
        }
      `;
      document.head.appendChild(style);
    }

    bars.forEach((bar) => {
      let rotator = bar.querySelector('[data-briutly-announcement-rotator]');
      if (!rotator) {
        bar.textContent = '';
        rotator = document.createElement('span');
        rotator.setAttribute('data-briutly-announcement-rotator', '');
        bar.appendChild(rotator);
      }

      if (bar.dataset.briutlyRotatorReady === 'true') return;
      bar.dataset.briutlyRotatorReady = 'true';
      let index = 0;
      rotator.textContent = messages[index];

      window.setInterval(() => {
        index = (index + 1) % messages.length;
        rotator.classList.add('is-changing');
        window.setTimeout(() => {
          rotator.textContent = messages[index];
          rotator.classList.remove('is-changing');
        }, 220);
      }, 2800);
    });
  };

  const start = () => window.initBriutlyAnnouncementRotator();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  window.addEventListener('load', start);
  window.setTimeout(start, 300);
  window.setTimeout(start, 1200);
})();
